// React Dependencies
import React, { useRef, useState, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useInfiniteScroll } from 'ahooks';
import { Search as SearchIcon } from '@material-ui/icons';
import {
  Button, Card, CardHeader, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Divider, Grid, List, ListItem, ListItemIcon, ListItemText, makeStyles, Typography, InputAdornment,
} from '@material-ui/core';

// Internal Dependencies
import OverLoading from '../../../global/OverLoading';
import useDebounce from '../../../global/use-debounce';
import Colours from '../../../../styles/colours';
import { Status } from '../../../../common/constants';
import { IsEmpty } from '../../../../common/checks';
import { Sleep } from '../../../../common/util';

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width:           '100%',
    height:          230,
    backgroundColor: theme.palette.background.paper,
    overflow:        'auto',
  },
}));

const TrailItemDialog = ({ handleSave, onClose, open, idKey, context, fetchLeft, fetchRight }) => {
  const classes = useStyles();
  const leftSectionRef = useRef(null);

  const [searchInput, setSearchInput] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [checked, setChecked] = useState([]);

  // Infinite scroll states
  const [isNoMoreData, setIsNoMoreData] = useState(false);
  const [pageNo, setPageNo] = useState(0);

  const debouncedSearchInput = useDebounce(searchInput, 500);

  const getIndex = (list, data) => list.findIndex((d) => d[idKey] === data[idKey]);
  const not = (a, b) => a.filter((item) => getIndex(b, item) === -1);
  const intersection = (a, b) => a.filter((item) => getIndex(b, item) !== -1);
  const union = (a, b) => [...a, ...not(b, a)];

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const { loading: isLoadingData, loadingMore: isLoadingMoreData, reloadAsync: reloadData } = useInfiniteScroll(
    async () => {
      try {
        await Sleep(700);

        const pageSize = 15;
        const params = { pageNo, pageSize, status: Status.Live };
        if (!IsEmpty(searchInput)) {
          params.title = searchInput;
        }

        let { rows: dataList } = await fetchLeft(params);
        dataList = dataList.map((d) => ({ ...d, [idKey]: d.id }));

        if (IsEmpty(right)) {
          const { rows: trailItems } = await fetchRight();
          const newLeft = not([...left, ...dataList], trailItems);
          setLeft(newLeft);
          setRight(trailItems);
          setPageCount(newLeft.length);
        } else {
          const newLeft = not([...left, ...dataList], right);
          setLeft(newLeft);
          setPageCount(newLeft.length);
        }

        setPageNo(pageNo + 1);
        setIsNoMoreData(dataList.length < pageSize);
        return { list: dataList };
      } catch (err) {
        toast.error(err.message);
        return { list: [] };
      }
    },
    {
      target:   leftSectionRef.current,
      isNoMore: () => isNoMoreData,
      onError:  () => setIsNoMoreData(true),
    },
  );

  const handleToggle = (value) => () => {
    const currentIndex = getIndex(checked, value);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setChecked(not(checked, leftChecked));
    setLeft(not(left, leftChecked));
    setPageCount(pageCount - leftChecked.length);
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
    setPageCount(pageCount + rightChecked.length);
  };

  const dataList = (title, items, itemsCount, ref = null) => (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        avatar={(
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
          />
        )}
        title={title}
        subheader={`${numberOfChecked(items)}/${IsEmpty(itemsCount) ? items.length : itemsCount} selected`}
      />
      <Divider />
      <List className={classes.list} dense component="div" role="list" innerRef={ref}>
        {items.map((data) => (
          <ListItem key={data[idKey]} role="listitem" button onClick={handleToggle(data)}>
            <ListItemIcon>
              <Checkbox
                checked={getIndex(checked, data) !== -1}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText
              id={`data-${data[idKey]}`}
              primary={data.title}
              secondary={`${context} ID: ${data[idKey]}`}
            />
          </ListItem>
        ))}
        {isLoadingMoreData && !IsEmpty(ref) && !IsEmpty(ref.current) && (
          <Typography variant="subtitle1" className="ml-4" style={{ color: Colours.Gray1 }}>
            Loading more...
          </Typography>
        )}
      </List>
    </Card>
  );

  useEffect(() => {
    setPageNo(0);
    setIsNoMoreData(false);
    setLeft([]);
  }, [debouncedSearchInput]);

  useEffect(() => {
    if (pageNo === 0) {
      reloadData();
    }
  }, [pageNo]);

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={onClose}>
      {isLoadingData && <OverLoading />}
      <DialogTitle>
        <Typography variant="h5" component="div" style={{ fontWeight: 'bold' }}>{`Manage ${context} Trail Items`}</Typography>
      </DialogTitle>
      <DialogContent>
        <div className="w-100 mb-4">
          <TextField
            placeholder="Search"
            className="mb-2"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Grid
            container
            justifycontent="flex-start"
            alignItems="center"
            className="m-auto"
          >
            <Grid xs={5} className="p-0" item>
              {dataList(`Available ${context}s`, left, pageCount, leftSectionRef)}
            </Grid>
            <Grid xs={2} item>
              <Grid container direction="column" alignItems="center">
                <Button
                  variant="outlined"
                  onClick={handleCheckedRight}
                  disabled={leftChecked.length === 0}
                >
                  {'>'}
                </Button>
                <div className="mb-2" />
                <Button
                  variant="outlined"
                  onClick={handleCheckedLeft}
                  disabled={rightChecked.length === 0}
                >
                  {'<'}
                </Button>
              </Grid>
            </Grid>
            <Grid xs={5} className="p-0" item>
              {dataList(`Current ${context} Trail Items`, right)}
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="d-flex">
          <Button
            variant="contained"
            className="mr-2"
            color="primary"
            onClick={() => handleSave(right.map((data) => data[idKey]))}
          >
            Save
          </Button>
          <Button variant="text" onClick={onClose}>Cancel</Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

TrailItemDialog.propTypes = {
  handleSave: PropTypes.func.isRequired,
  onClose:    PropTypes.func.isRequired,
  open:       PropTypes.bool.isRequired,
  idKey:      PropTypes.string.isRequired,
  context:    PropTypes.string.isRequired,
  fetchLeft:  PropTypes.func.isRequired,
  fetchRight: PropTypes.func.isRequired,
};

export default TrailItemDialog;
